import React, { useState, useEffect } from 'react';
import { Search, X } from 'lucide-react';

const PropertyModal = ({ isOpen, onClose, onSave, selectedProperties, defaultProperties, isDark, authFetch }) => {
  const [properties, setProperties] = useState({});
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set(selectedProperties));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);
  
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await authFetch('/api/properties');
        if (!response.ok) throw new Error('Failed to fetch properties');
        const data = await response.json();
        setProperties(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchProperties();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const toggleProperty = (propName) => {
    if (defaultProperties.includes(propName)) { return; }
    
    const newSelected = new Set(selected);
    if (newSelected.has(propName)) {
      newSelected.delete(propName);
    } else {
      newSelected.add(propName);
    }
    setSelected(newSelected);
  };

  const filteredProperties = Object.entries(properties).reduce((acc, [group, props]) => {
    // Check if either the group name matches OR any properties match
    if (group.toLowerCase().includes(search.toLowerCase()) || 
        props.some(prop => 
          prop.label.toLowerCase().includes(search.toLowerCase()) ||
          prop.name.toLowerCase().includes(search.toLowerCase())
        )) {
      // If group matches, include all properties
      // If properties match, only include matching ones
      const filteredProps = group.toLowerCase().includes(search.toLowerCase())
        ? props
        : props.filter(prop => 
            prop.label.toLowerCase().includes(search.toLowerCase()) ||
            prop.name.toLowerCase().includes(search.toLowerCase())
          );
      
      acc[group] = filteredProps;
    }
    return acc;
  }, {});

  const allProperties = Object.values(properties).flat();

  const selectedPropertiesDetails = allProperties
    .filter(prop => selected.has(prop.name) || defaultProperties.includes(prop.name))
    .sort((a, b) => {
      // Sort default properties first
      const aIsDefault = defaultProperties.includes(a.name);
      const bIsDefault = defaultProperties.includes(b.name);
      if (aIsDefault && !bIsDefault) return -1;
      if (!aIsDefault && bIsDefault) return 1;
      return a.label.localeCompare(b.label);
    });

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div
        className={`w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col rounded-lg
        ${isDark ? 'bg-gray-800' : 'bg-white'}`}
        onClick={e => e.stopPropagation()}
      >
        
        {/* Header */}
        <div className={`p-4 border-b flex justify-between items-center
          ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <h2 className={`text-lg font-semibold
            ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Select Properties
          </h2>
          <button 
            onClick={onClose} 
            className={`${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Search */}
        <div className={`p-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="relative">
            <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5
              ${isDark ? 'text-gray-400' : 'text-gray-400'}`} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search properties..."
              className={`w-full pl-10 pr-4 py-2 border rounded-lg
                ${isDark 
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className={`p-4 text-center ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Loading properties...
            </div>
          ) : error ? (
            <div className={`p-4 text-center ${isDark ? 'text-red-400' : 'text-red-500'}`}>
              {error}
            </div>
          ) : (
            <div className="p-4">
              {/* Selected Properties Section */}
              <div className="mb-8">
                <h3 className={`text-sm font-semibold mb-2
                  ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                  Selected Properties
                </h3>
                <div className="space-y-2">
                  {selectedPropertiesDetails.map((prop) => (
                    <label
                      key={prop.name}
                      className={`flex items-center space-x-3 p-2 rounded
                        ${defaultProperties.includes(prop.name) 
                          ? isDark ? 'bg-gray-700/50' : 'bg-gray-100' 
                          : isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}
                        ${defaultProperties.includes(prop.name) ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <input
                        type="checkbox"
                        checked={true}
                        onChange={() => toggleProperty(prop.name)}
                        disabled={defaultProperties.includes(prop.name)}
                        className={`h-4 w-4 rounded border
                          ${isDark 
                            ? 'bg-gray-700 border-gray-600' 
                            : 'border-gray-300'}
                          disabled:opacity-50`}
                      />
                      <span className={`text-sm flex-1
                        ${defaultProperties.includes(prop.name) ? 'opacity-75' : ''}
                        ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {prop.label}
                        {defaultProperties.includes(prop.name) ? (
                          <span className="ml-2 text-xs text-gray-500">(default)</span>
                        ) : (
                          <span className={`ml-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            ({prop.group})
                          </span>
                        )}
                      </span>
                    </label>
                  ))}
                  {selectedPropertiesDetails.length === 0 && (
                    <div className={`text-sm italic ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      No additional properties selected
                    </div>
                  )}
                </div>
              </div>
              
              {/* Divider */}
              <div className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'} mb-8`} />

              {/* Available Properties */}
              {Object.entries(filteredProperties).map(([group, props]) => (
                <div key={group} className="mb-6">
                  <h3 className={`text-sm font-semibold mb-2
                    ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                    {group}
                  </h3>
                  <div className="space-y-2">
                    {props.map((prop) => (
                      <label
                        key={prop.name}
                        className={`flex items-center space-x-3 p-2 rounded
                          ${defaultProperties.includes(prop.name) 
                            ? isDark ? 'bg-gray-700/50' : 'bg-gray-100' 
                            : isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}
                          ${defaultProperties.includes(prop.name) 
                            ? 'cursor-not-allowed' : 'cursor-pointer'}
                        `}
                      >
                        <input
                          type="checkbox"
                          checked={selected.has(prop.name) || defaultProperties.includes(prop.name)}
                          onChange={() => toggleProperty(prop.name)}
                          disabled={defaultProperties.includes(prop.name)}
                          className={`h-4 w-4 rounded border
                            ${isDark 
                              ? 'bg-gray-700 border-gray-600' 
                              : 'border-gray-300'}
                            disabled:opacity-50
                          `}
                        />
                        <span className={`text-sm ${defaultProperties.includes(prop.name) ? 'opacity-75' : ''}
                          ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          {prop.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`p-4 border-t flex justify-end space-x-3
          ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            onClick={onClose}
            className={`px-4 py-2 border rounded-lg
              ${isDark 
                ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(Array.from(selected))}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default PropertyModal;